import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Radar, MapPin, Crosshair, Zap, LocateFixed, Search, Loader2, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import RadarView from "@/components/RadarView";
import JobCard from "@/components/JobCard";
import JobDrawer from "@/components/JobDrawer";
import useJobActions from "@/hooks/useJobActions";
import api from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CITIES = {
  "San Francisco, CA": [37.7749, -122.4194],
  "New York, NY": [40.7128, -74.006],
  "Austin, TX": [30.2672, -97.7431],
  "Seattle, WA": [47.6062, -122.3321],
};

const LEGEND = [
  ["#2DD4BF", "radar.legendMatch"],
  ["#F59E0B", "radar.legendSalary"],
  ["#8B5CF6", "radar.legendRemote"],
  ["#EF4444", "radar.legendUrgent"],
];

export default function RadarSearch() {
  const { t } = useTranslation();
  const [place, setPlace] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem("wo_radar_place")); if (saved && saved.lat) return saved; } catch {}
    return { lat: 37.7749, lng: -122.4194, label: "San Francisco" };
  });
  const [radius, setRadius] = useState(25);
  const [jobType, setJobType] = useState("all");
  const [workMode, setWorkMode] = useState("all");
  const [livePulse, setLivePulse] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const { savedIds, appliedIds, toggleSave, apply } = useJobActions();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs/radar", { params: { lat: place.lat, lng: place.lng, radius_km: radius, job_type: jobType, work_mode: workMode } });
      setJobs(data);
    } finally { setLoading(false); }
  }, [place.lat, place.lng, radius, jobType, workMode]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  // Persist the chosen location so refreshes keep it (no reset to San Francisco)
  useEffect(() => {
    try { localStorage.setItem("wo_radar_place", JSON.stringify(place)); } catch {}
  }, [place]);

  // Auto-use live location only on the very first visit (when nothing was saved yet)
  const hadSavedPlace = useRef(Boolean(localStorage.getItem("wo_radar_place")));
  useEffect(() => {
    if (hadSavedPlace.current) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setPlace({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "My location" }),
      () => {},
      { timeout: 8000 }
    );
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported by your browser"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPlace({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "My location" });
        toast.success("Radar locked to your location");
        setLocating(false);
      },
      (err) => {
        toast.error(err.code === 1 ? "Location permission denied" : "Couldn't get your location");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const geocode = async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=6&language=en&format=json`);
      const data = await res.json();
      setResults(data.results || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  useEffect(() => { const t = setTimeout(() => geocode(query), 350); return () => clearTimeout(t); }, [query]);

  const pickResult = (r) => {
    const label = [r.name, r.admin1, r.country_code].filter(Boolean).join(", ");
    setPlace({ lat: r.latitude, lng: r.longitude, label: r.name });
    setQuery(label);
    setResults([]);
    toast.success(`Scanning around ${r.name}`);
  };

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
            <Radar size={20} />
          </div>
          <div>
            <h1 className="font-display font-bold text-3xl text-slate-100">{t("radar.title")}</h1>
            <p className="text-slate-400 text-sm">
              <span className="font-mono text-teal-400" data-testid="radar-count">{jobs.length}</span> {t("radar.jobsWithin")} {radius}km {t("radar.of")} {place.label}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          {/* radar + controls */}
          <div>
            <div className="liquid-glass rounded-2xl p-6 sm:p-10">
              <RadarView jobs={livePulse ? jobs : jobs} radiusKm={radius} onSelect={setSelected} selectedId={selected?.id} />
            </div>

            {/* control bar */}
            <div className="liquid-glass rounded-2xl p-5 mt-5 space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><MapPin size={12} /> {t("radar.location")}</label>
                  <div className="mt-1.5 flex gap-2">
                    <div className="relative flex-1">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                      <Input
                        data-testid="radar-location-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("radar.searchPh")}
                        className="pl-9 pr-8 bg-slate-950/50 border-slate-700 text-slate-200"
                      />
                      {query && (
                        <button onClick={() => { setQuery(""); setResults([]); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                          <X size={14} />
                        </button>
                      )}
                      {(searching || results.length > 0) && query.length >= 2 && (
                        <div className="absolute z-40 mt-1 w-full glass border border-slate-700 rounded-lg overflow-hidden max-h-60 overflow-y-auto" data-testid="radar-location-results">
                          {searching && <div className="px-3 py-2.5 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> {t("radar.searchingPlaces")}</div>}
                          {!searching && results.map((r) => (
                            <button key={`${r.id}`} onClick={() => pickResult(r)} data-testid="radar-location-result-item"
                              className="w-full text-left px-3 py-2.5 text-sm text-slate-200 hover:bg-teal-500/10 flex items-center gap-2 border-b border-slate-800 last:border-0">
                              <MapPin size={13} className="text-teal-400 shrink-0" />
                              <span className="truncate">{r.name}{r.admin1 ? `, ${r.admin1}` : ""} <span className="text-slate-500">· {r.country}</span></span>
                            </button>
                          ))}
                          {!searching && results.length === 0 && <div className="px-3 py-2.5 text-sm text-slate-500">{t("radar.noPlaces")}</div>}
                        </div>
                      )}
                    </div>
                    <Button onClick={useMyLocation} disabled={locating} data-testid="radar-use-my-location"
                      className="shrink-0 bg-teal-500/15 border border-teal-500/30 text-teal-300 hover:bg-teal-500/25">
                      {locating ? <Loader2 size={16} className="animate-spin" /> : <LocateFixed size={16} />}
                      <span className="hidden sm:inline ml-2">{t("radar.myLocation")}</span>
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Object.entries(CITIES).map(([name, [la, ln]]) => (
                      <button key={name} data-testid="radar-preset-city"
                        onClick={() => { setPlace({ lat: la, lng: ln, label: name.split(",")[0] }); setQuery(""); }}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${place.label === name.split(",")[0] ? "border-teal-500/50 bg-teal-500/10 text-teal-300" : "border-slate-700 text-slate-400 hover:border-slate-600"}`}>
                        {name.split(",")[0]}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><Crosshair size={12} /> {t("radar.radius")}: <span className="text-teal-400">{radius} km</span></label>
                  <Slider data-testid="radar-radius-slider" value={[radius]} onValueChange={([v]) => setRadius(v)} min={1} max={50} step={1} className="mt-4" />
                  <p className="text-xs text-slate-500 mt-3 font-mono">lat {place.lat.toFixed(3)}, lng {place.lng.toFixed(3)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-slate-500">{t("radar.jobType")}</label>
                  <Select value={jobType} onValueChange={setJobType}>
                    <SelectTrigger data-testid="radar-type-select" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass border-slate-700 text-slate-200">
                      <SelectItem value="all">{t("radar.allTypes")}</SelectItem>
                      {["Full-time", "Part-time", "Contract", "Internship", "Freelance"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-slate-500">{t("radar.workMode")}</label>
                  <Select value={workMode} onValueChange={setWorkMode}>
                    <SelectTrigger data-testid="radar-mode-select" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass border-slate-700 text-slate-200">
                      <SelectItem value="all">{t("radar.allModes")}</SelectItem>
                      {["Remote", "Onsite", "Hybrid"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-slate-950/50 border border-slate-700 px-4 py-2.5">
                  <span className="text-sm text-slate-300 flex items-center gap-1.5"><Zap size={14} className="text-teal-400" /> {t("radar.livePulse")}</span>
                  <Switch data-testid="radar-live-pulse-toggle" checked={livePulse} onCheckedChange={setLivePulse} />
                </div>
              </div>

              <div className="flex flex-wrap gap-4 pt-1">
                {LEGEND.map(([c, l]) => (
                  <span key={l} className="flex items-center gap-1.5 text-xs text-slate-400">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: c, boxShadow: `0 0 6px ${c}` }} /> {t(l)}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* nearby list */}
          <div className="space-y-3">
            <h3 className="font-display font-semibold text-slate-200 flex items-center gap-2"><MapPin size={16} /> {t("radar.nearby")}</h3>
            {loading ? (
              <p className="text-slate-500 text-sm">{t("radar.scanning")}</p>
            ) : jobs.length === 0 ? (
              <div className="liquid-glass rounded-xl p-6 text-center text-slate-500 text-sm" data-testid="radar-empty">{t("radar.empty")}</div>
            ) : (
              <div className="space-y-3 max-h-[720px] overflow-y-auto pr-1">
                {jobs.map((job) => (
                  <JobCard key={job.id} job={job} saved={savedIds.includes(job.id)} onToggleSave={toggleSave} onOpen={setSelected} showDistance />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <JobDrawer job={selected} open={!!selected} onClose={() => setSelected(null)}
        saved={selected && savedIds.includes(selected.id)} onToggleSave={toggleSave} onApply={apply}
        applied={selected && appliedIds.includes(selected.id)} />
    </div>
  );
}
